import { DatePipe } from '@angular/common';
import {
    Component,
    inject,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import {
    FuseNavigationService,
    FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { TranslatePipe } from '@ngx-translate/core';
import { AuthService } from 'app/core/auth/auth.service';
import { RoleList } from 'app/core/enums';
import { User } from 'app/core/models/filmmaker-user.model';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { TenantConfigService } from 'app/core/tenant';
import { LanguageSelectorComponent } from 'app/layout/common/language-selector/language-selector.component';
import { SlateConfigService } from 'app/layout/common/slate/slate-config.service';
import { SlatePanelComponent } from 'app/layout/common/slate/slate-panel.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { FilmMakerWalletService, LevelInfo } from 'app/shared/services/film-maker-wallet.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'dense-layout',
    templateUrl: './dense.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        FuseLoadingBarComponent,
        FuseVerticalNavigationComponent,
        MatButtonModule,
        MatIconModule,
        FuseFullscreenComponent,
        UserComponent,
        RouterOutlet,
        LanguageSelectorComponent,
        TranslatePipe,
        DatePipe,
        MatTooltipModule,
        SlatePanelComponent,
    ],
})
export class DenseLayoutComponent implements OnInit, OnDestroy {
    @ViewChild(SlatePanelComponent) slatePanel?: SlatePanelComponent;

    isScreenSmall: boolean;
    navigation: Navigation;
    user: User | null = null;
    navigationAppearance: 'default' | 'dense' = 'dense';
    private _unsubscribeAll: Subject<void> = new Subject<void>();
    RoleList = RoleList;

    readonly tenantConfig = inject(TenantConfigService);

    /**
     * Resolved feature flags for this tenant. Computed once from the already-
     * initialized TenantConfigService — safe to call in the constructor and template.
     */
    get features() {
        return this.tenantConfig.getFeatures();
    }

    /** From `environment.askTalkieEnabled` — hide Ask Talkie chrome when false. */
    askTalkieEnabled = true;

    // Wallet balance properties
    walletBalance = 0;
    currencySymbol = '₹';
    isLoadingWallet = false;

    // Reel balance properties
    reelBalance = 0;
    isLoadingReel = false;

    // Level properties
    currentLevel: LevelInfo | null = null;
    nextLevel: LevelInfo | null = null;
    isLoadingLevel = false;

    date = new Date();

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _authService: AuthService,
        private _filmMakerWalletService: FilmMakerWalletService,
        private _slateConfig: SlateConfigService
    ) {
        this.askTalkieEnabled = this._slateConfig.askTalkieEnabled;
    }

    toggleAskTalkie(): void {
        this.slatePanel?.toggle();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                this.navigation = navigation;
            });

        // Subscribe to the user service
        this._authService.currentUserSubject
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: User | null) => {
                this.user = user;
                // Load wallet balance if user is a filmmaker
                if (user?.otherRoles?.includes(RoleList.FilmMaker)) {
                    this.loadWalletBalance();
                }
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');

                // Change the navigation appearance
                this.navigationAppearance = this.isScreenSmall
                    ? 'default'
                    : 'dense';
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Load wallet balance for filmmaker
     */
    loadWalletBalance(): void {
        this.isLoadingWallet = true;
        this.isLoadingReel = true;
        this.isLoadingLevel = true;
        this._filmMakerWalletService
            .getWalletBalance()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (response) => {
                    if (response.data) {
                        // Wallet balance
                        this.walletBalance =
                            response.data.walletBalance?.totalBalance || 0;

                        this._filmMakerWalletService.setWalletBalance(
                            response.data.walletBalance
                        );

                        // Level data
                        this.currentLevel = response.data.level?.currentLevel;
                        this.nextLevel = response.data.level?.nextLevel;
                        this._filmMakerWalletService.setLevel(
                            response.data.level
                        );

                        // Currency symbol
                        this.currencySymbol =
                            response.data.walletBalance?.currencySymbol || '₹';

                        // Reel balance
                        this.reelBalance =
                            response.data.reelBalance?.totalBalance || 0;

                        this._filmMakerWalletService.setReelBalance(
                            response.data.reelBalance
                        );
                    }
                    this.isLoadingWallet = false;
                    this.isLoadingReel = false;
                    this.isLoadingLevel = false;
                },
                error: (error) => {
                    console.error('Error loading wallet balance:', error);
                    this.isLoadingWallet = false;
                    this.isLoadingReel = false;
                    this.isLoadingLevel = false;
                },
            });
    }

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation =
            this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
                name
            );

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }
    navigateToWallet(type: string): void {
        this._router.navigate(['/film-maker/transaction-history'], {
            queryParams: {
                balanceType: type,
            },
        });
    }

    /**
     * Toggle the navigation appearance
     */
    toggleNavigationAppearance(): void {
        this.navigationAppearance =
            this.navigationAppearance === 'default' ? 'dense' : 'default';
    }
}
